import { Table } from 'antd';
import { Truck } from 'lucide-react';
import { Link } from 'react-router';
import { EmptyState } from '../../components/EmptyState';
import type { OwnerTrailer, OwnerTruck } from './owners.api';

interface OwnerAssetsTabProps {
  trucksCurrent: OwnerTruck[];
  trucksPast: OwnerTruck[];
  trailersCurrent: OwnerTrailer[];
  trailersPast: OwnerTrailer[];
}

function truckColumns(showEffectiveTo: boolean) {
  const columns = [
    {
      title: 'Truck',
      key: 'truck',
      render: (_: unknown, truck: OwnerTruck) => (
        <Link className="font-medium text-link hover:underline" to={`/fleet/${truck.id}`}>
          {truck.registration}
        </Link>
      ),
    },
    { title: 'Make / model', key: 'makeModel', render: (_: unknown, truck: OwnerTruck) => `${truck.make} ${truck.model}` },
    { title: 'Owned since', dataIndex: 'effectiveFrom', key: 'effectiveFrom', render: (date: string) => new Date(date).toLocaleDateString() },
  ];
  if (showEffectiveTo) {
    columns.push({ title: 'Owned until', dataIndex: 'effectiveTo', key: 'effectiveTo', render: (date: string) => new Date(date).toLocaleDateString() });
  }
  return columns;
}

function trailerColumns(showEffectiveTo: boolean) {
  const columns = [
    {
      title: 'Trailer',
      key: 'trailer',
      render: (_: unknown, trailer: OwnerTrailer) => (
        <Link className="font-medium text-link hover:underline" to={`/trailers/${trailer.id}`}>
          {trailer.registration}
        </Link>
      ),
    },
    { title: 'Type', dataIndex: 'typeCode', key: 'typeCode' },
    { title: 'Owned since', dataIndex: 'effectiveFrom', key: 'effectiveFrom', render: (date: string) => new Date(date).toLocaleDateString() },
  ];
  if (showEffectiveTo) {
    columns.push({ title: 'Owned until', dataIndex: 'effectiveTo', key: 'effectiveTo', render: (date: string) => new Date(date).toLocaleDateString() });
  }
  return columns;
}

/**
 * An owner's trucks and trailers, current and past — an owner can hold both (gitaumoses4/chukua#15).
 * The periods themselves are the source, so no history/audit reconstruction is needed here.
 */
export function OwnerAssetsTab({ trucksCurrent, trucksPast, trailersCurrent, trailersPast }: OwnerAssetsTabProps) {
  if (trucksCurrent.length === 0 && trucksPast.length === 0 && trailersCurrent.length === 0 && trailersPast.length === 0) {
    return <EmptyState icon={Truck} title="No trucks or trailers yet" description="Trucks and trailers transferred to this owner will show up here." />;
  }

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h3 className="mb-2 text-sm font-medium text-muted">Current trucks</h3>
        <Table rowKey="id" dataSource={trucksCurrent} columns={truckColumns(false)} pagination={false} locale={{ emptyText: 'No current trucks' }} />
      </div>
      <div>
        <h3 className="mb-2 text-sm font-medium text-muted">Past trucks</h3>
        <Table rowKey="id" dataSource={trucksPast} columns={truckColumns(true)} pagination={false} locale={{ emptyText: 'No past trucks' }} />
      </div>
      <div>
        <h3 className="mb-2 text-sm font-medium text-muted">Current trailers</h3>
        <Table rowKey="id" dataSource={trailersCurrent} columns={trailerColumns(false)} pagination={false} locale={{ emptyText: 'No current trailers' }} />
      </div>
      <div>
        <h3 className="mb-2 text-sm font-medium text-muted">Past trailers</h3>
        <Table rowKey="id" dataSource={trailersPast} columns={trailerColumns(true)} pagination={false} locale={{ emptyText: 'No past trailers' }} />
      </div>
    </div>
  );
}
